import React, { useEffect, useState } from 'react';
import { DatePicker } from 'hamro-nepali-patro';
import { Container, Paper, Typography, Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox, TextField, MenuItem, Select, FormControl, InputLabel, Alert, Radio, RadioGroup, FormControlLabel } from '@mui/material';
import api from '../../services/api';
import {
  Add,
  Remove,
  ArrowBack
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';

const WorkReceipt = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialKarigar = params.get('karigar') || '';
  const initialKarat = params.get('karat') || '';

  const [karigar, setKarigar] = useState(initialKarigar);
  const [karigars, setKarigars] = useState([]);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [remarks, setRemarks] = useState('');
  const [receiveDate, setReceiveDate] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [karatFilter, setKaratFilter] = useState(initialKarat);
  const [sourceFilter, setSourceFilter] = useState('both');
  const [rowData, setRowData] = useState({});

  useEffect(() => {
    const fetchKarigars = async () => {
      try {
        const res = await api.get('/api/karigar');
        setKarigars(res.data);
      } catch { }
    };
    fetchKarigars();
  }, []);

  useEffect(() => {
    const fetchItems = async () => {
      if (!karigar) return setItems([]);
      try {
        let orderItems = [];
        let directOrderItems = [];
        if (sourceFilter === 'order' || sourceFilter === 'both') {
          const res = await api.get(`/api/karigar/work-issued?karigar=${karigar}`);
          orderItems = res.data || [];
        }
        if (sourceFilter === 'direct' || sourceFilter === 'both') {
          // Try to fetch direct order work-issued items (endpoint must exist)
          try {
            const resDirect = await api.get(`/api/karigar/direct-work-issued?karigar=${karigar}`);
            directOrderItems = resDirect.data || [];
          } catch { }
        }
        setItems([...orderItems, ...directOrderItems]);
      } catch {
        setItems([]);
      }
    };
    fetchItems();
  }, [karigar, sourceFilter]);

  const handleSelect = (itemId) => {
    // If itemId is an object, extract _id
    const id = typeof itemId === 'object' && itemId._id ? itemId._id : itemId;
    setSelected((prev) => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!karigar || selected.length === 0 || !receiveDate) {
      setError('Please select karigar, items, and receipt date.');
      return;
    }
    setLoading(true);
    try {
      // Prepare per-item data for selected items
      const itemsData = selected.map((itemId, index) => {
        const data = rowData[itemId] || {};
        return {
          itemId: String(itemId),
          receiptWeight: data.receiptWeight,
          gsbWeight: data.gsbWeight,
          wastage: data.wastage,
          receiptVoucherNumber: data.receiptVoucherNumber || `RCT_${Date.now()}_${index}`,
          receiptDate: data.receiptDate || receiveDate,
          remarks,
        };
      });
      await api.post('/api/karigar/work-receipt', {
        karigar,
        items: itemsData,
        receiveDate,
        remarks
      });
      setSuccess('Work receipt submitted successfully!');
      setSelected([]);
      setRemarks('');
      setReceiveDate('');
      // Refresh items
      const res = await api.get(`/api/karigar/work-issued?karigar=${karigar}`);
      setItems(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit work receipt');
    }
    setLoading(false);
  };

  const handleRowChange = (itemId, field, value) => {
    setRowData(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value
      }
    }));
  };

  const filteredItems = items
  .filter(item => {
    // Ensure item exists and has required properties
    if (!item || !item._id || !item.itemName || !item.billName) {
      return false;
    }
    // Apply filters only on valid items
    if (karatFilter && item.karat !== karatFilter) return false;
    if (!item.itemWeight || !item.quantity) return false;
    return true;
  });

  return (
    <Container sx={{ py: 4, px: { xs: 0, sm: 2 } }} maxWidth={false} disableGutters>
      <Paper elevation={3} sx={{ p: { xs: 1, sm: 3 }, width: '100%' }}>
        <Button startIcon={<ArrowBack />} onClick={() => window.history.back()} variant="outlined" color="primary" sx={{ mb: 2 }}>
          Back
        </Button>

        <Typography variant="h4" gutterBottom color="primary">
          Work Receipt
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        <form onSubmit={handleSubmit}>
          {/* Compact row for Karigar, Karat, Date, Remarks */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <FormControl required sx={{ minWidth: 160, flex: '1 1 160px' }} size="small">
              <InputLabel>Karigar</InputLabel>
              <Select value={karigar} label="Karigar" onChange={e => setKarigar(e.target.value)}>
                {karigars.map(k => (
                  <MenuItem key={k.abbreviation} value={k.abbreviation}>{k.abbreviation} - {k.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 120, flex: '1 1 120px' }} size="small">
              <InputLabel>Karat</InputLabel>
              <Select value={karatFilter} label="Karat" onChange={e => setKaratFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                {['22K', '24K', '22/22K', '24/24K', '18K', 'PT95', 'WG'].map(karat => (
                  <MenuItem key={karat} value={karat}>{karat}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Source Filter */}
            <FormControl sx={{ minWidth: 140, flex: '1 1 140px' }} size="small">
              <InputLabel>Source</InputLabel>
              <Select value={sourceFilter} label="Source" onChange={e => setSourceFilter(e.target.value)}>
                <MenuItem value="both">Both</MenuItem>
                <MenuItem value="order">Order</MenuItem>
                <MenuItem value="direct">Direct Order</MenuItem>
              </Select>
            </FormControl>
            <Box>
              <DatePicker
                value={receiveDate}
                onChange={date => setReceiveDate(date)}
                inputProps={{ fullWidth: true, size: 'small' }}
                sx={{ minWidth: 140, flex: '1 1 140px' }}
              />
            </Box>
            <TextField
              label="Remarks"
              value={remarks}
              onChange={e => setRemarks(e.target.value)}
              size="small"
              sx={{ minWidth: 200, flex: '2 1 200px' }}
            />
          </Box>

          {/* Calculation tables row */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, width: '100%', flexWrap: 'wrap' }}>
            {/* Total Received Calculation Table */}
            <TableContainer component={Paper} sx={{ flex: 2, minWidth: 400 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Work Received Wt</TableCell>
                    <TableCell>Weight Received</TableCell>
                    <TableCell>GSB Weight</TableCell>
                    <TableCell>Net Wt Received</TableCell>
                    <TableCell>Net Wt Balance</TableCell>
                    <TableCell>Wastage %</TableCell>
                    <TableCell>Wastage</TableCell>
                    <TableCell>Metal Rcvd Wt</TableCell>
                    <TableCell>Receipt Balance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    {/* Calculation logic for each column */}
                    {(() => {
                      // Get selected items marked as received
                      const receivedItems = filteredItems.filter(item => (rowData[item._id]?.received === 'yes'));
                      // Work Received Weight: sum of itemWeight for received items
                      const workReceivedWeight = receivedItems.reduce((sum, item) => sum + (parseFloat(item.itemWeight) || 0), 0);
                      // Weight Received: sum of manual entry (receiptWeight)
                      const weightReceived = receivedItems.reduce((sum, item) => sum + (parseFloat(rowData[item._id]?.receiptWeight) || 0), 0);
                      // GSB Weight: sum of gsbWeight for received items
                      const gsbWeight = receivedItems.reduce((sum, item) => sum + (parseFloat(rowData[item._id]?.gsbWeight) || 0), 0);
                      // Net Weight Received: Weight Received + GSB Weight
                      const netWeightReceived = weightReceived + gsbWeight;
                      // Net Weight Balance: Work Received Weight - Weight Received
                      const netWeightBalance = workReceivedWeight - weightReceived;
                      // Wastage %: average of wastage % for received items
                      let wastagePercents = receivedItems.map(item => {
                        let w = item.wastage;
                        if (typeof w === 'string' && w.endsWith('%')) w = parseFloat(w);
                        else if (w <= 1 && w > 0) w = w * 100;
                        return parseFloat(w) || 0;
                      });
                      const wastagePercent = wastagePercents.length ? (wastagePercents.reduce((a, b) => a + b, 0) / wastagePercents.length) : 0;
                      // Wastage: wastage % of Weight Received
                      const wastage = (wastagePercent / 100) * weightReceived;
                      // Metal Received Weight: Weight Received + Wastage
                      const metalReceivedWeight = weightReceived + wastage;
                      // Receipt Balance: (Work Received Weight - Weight Received) - Wastage
                      const receiptBalance = netWeightBalance - wastage;
                      return [
                        workReceivedWeight,
                        weightReceived,
                        gsbWeight,
                        netWeightReceived,
                        netWeightBalance,
                        wastagePercent.toFixed(2) + '%',
                        wastage,
                        metalReceivedWeight,
                        receiptBalance
                      ].map((val, idx) => (
                        <TableCell key={idx}>{typeof val === 'number' && !isNaN(val) ? val.toFixed(2) : val}</TableCell>
                      ));
                    })()}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            {/* Act Gross/Net/Payable Wastage Table (as rows) */}
            <TableContainer component={Paper} sx={{ flex: 1, minWidth: 260, maxWidth: 320 }}>
              <Table size="small">
                <TableBody>
                  {(() => {
                    const receivedItems = filteredItems.filter(item => (rowData[item._id]?.received === 'yes'));
                    // Act Gross Wt Rcvd: sum of receiptWeight for received items
                    const actGrossWtRcvd = receivedItems.reduce((sum, item) => sum + (parseFloat(rowData[item._id]?.receiptWeight) || 0), 0);
                    // Act Net Wt Rcvd: actGrossWtRcvd + sum of gsbWeight
                    const actNetWtRcvd = actGrossWtRcvd + receivedItems.reduce((sum, item) => sum + (parseFloat(rowData[item._id]?.gsbWeight) || 0), 0);
                    // Act Payable Wastage: (average wastage % of received items) * actGrossWtRcvd
                    let wastagePercents = receivedItems.map(item => {
                      let w = item.wastage;
                      if (typeof w === 'string' && w.endsWith('%')) w = parseFloat(w);
                      else if (w <= 1 && w > 0) w = w * 100;
                      return parseFloat(w) || 0;
                    });
                    const wastagePercent = wastagePercents.length ? (wastagePercents.reduce((a, b) => a + b, 0) / wastagePercents.length) : 0;
                    const actPayableWastage = (wastagePercent / 100) * actGrossWtRcvd;
                    const rows = [
                      ['Act Gross Wt Rcvd', actGrossWtRcvd],
                      ['Act Net Wt Rcvd', actNetWtRcvd],
                      ['Act Payable Wastage', actPayableWastage]
                    ];
                    return rows.map(([label, val], idx) => (
                      <TableRow key={idx}>
                        <TableCell>{label}</TableCell>
                        <TableCell>{typeof val === 'number' && !isNaN(val) ? val.toFixed(2) : val}</TableCell>
                      </TableRow>
                    ));
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          <Typography variant="h6" sx={{ mt: 2 }}>Issued Items</Typography>
          <TableContainer sx={{ mb: 2, width: '100%' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Select</TableCell>
                  <TableCell>Bill Name</TableCell>
                  <TableCell>Bill Number</TableCell>
                  <TableCell>Item Name</TableCell>
                  <TableCell>Item Qty</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>GDB</TableCell>
                  <TableCell>Order Weight</TableCell>
                  <TableCell>Karat</TableCell>
                  <TableCell>Karigar</TableCell>
                  <TableCell>Received</TableCell>
                  <TableCell>Receipt Voucher #</TableCell>
                  <TableCell>Receipt Date</TableCell>
                  <TableCell>Receipt Weight</TableCell>
                  <TableCell>GSB Weight</TableCell>
                  <TableCell>Net Weight</TableCell>
                  <TableCell>Wt Difference</TableCell>
                  <TableCell>Act Wastage Paid</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredItems.length === 0 ? (
                  <TableRow><TableCell colSpan={18}>No work issued.</TableCell></TableRow>
                ) : filteredItems.map(item => {
                  const data = rowData[item._id] || {};
                  const receiptWeight = parseFloat(data.receiptWeight) || 0;
                  const gsbWeight = parseFloat(data.gsbWeight) || 0;
                  const netWeight = receiptWeight - gsbWeight;
                  const wtDifference = (parseFloat(item.itemWeight) || 0) - netWeight;
                  return (
                    <TableRow key={item._id}>
                      <TableCell>
                        <Checkbox checked={selected.includes(item._id)} onChange={() => handleSelect(item._id)} />
                      </TableCell>
                      <TableCell>{item.billName || ''}</TableCell>
                      <TableCell>{item.billNumber || ''}</TableCell>
                      <TableCell>{item.itemName || ''}</TableCell>
                      <TableCell>{item.quantity || ''}</TableCell>
                      <TableCell>{item.size || ''}</TableCell>
                      <TableCell>{item.description || ''}</TableCell>
                      <TableCell>{item.gdb || ''}</TableCell>
                      <TableCell>{item.itemWeight || ''}</TableCell>
                      <TableCell>{item.karat || ''}</TableCell>
                      <TableCell>{item.karigarAbbreviation || ''}</TableCell>
                      <TableCell>
                        <RadioGroup
                          row
                          value={data.received || 'no'}
                          onChange={e => handleRowChange(item._id, 'received', e.target.value)}
                        >
                          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                          <FormControlLabel value="no" control={<Radio />} label="No" />
                        </RadioGroup>
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={data.receiptVoucherNumber || `RCT_${String(filteredItems.findIndex(i => i._id === item._id) + 1).padStart(2, '0')}`}
                          onChange={e => handleRowChange(item._id, 'receiptVoucherNumber', e.target.value)}
                          size="small"
                          variant="outlined"
                          sx={{ width: 120 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <DatePicker
                            value={data.receiptDate || ''}
                            onChange={date => handleRowChange(item._id, 'receiptDate', date)}
                            inputProps={{ fullWidth: true, size: 'small' }}
                            sx={{ width: 130 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={data.receiptWeight || ''}
                          onChange={e => handleRowChange(item._id, 'receiptWeight', e.target.value)}
                          size="small"
                          variant="outlined"
                          sx={{ width: 90 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={data.gsbWeight || ''}
                          onChange={e => handleRowChange(item._id, 'gsbWeight', e.target.value)}
                          size="small"
                          variant="outlined"
                          sx={{ width: 90 }}
                        />
                      </TableCell>
                      <TableCell>{!isNaN(netWeight) ? netWeight : ''}</TableCell>
                      <TableCell>{!isNaN(wtDifference) ? wtDifference.toFixed(2) : ''}</TableCell>
                      <TableCell>{
                        item.wastage !== undefined && item.wastage !== null && item.itemWeight
                          ? (() => {
                            const wastage = parseFloat(item.wastage);
                            const itemWeight = parseFloat(item.itemWeight);



                            // If wastage is stored as a percentage value (e.g., 2 for 2%)
                            if (wastage > 0 && wastage <= 100) {
                              return wastage.toFixed(2).replace(/\.00$/, '') + '%';
                            }
                            // If wastage is stored as a decimal (e.g., 0.02 for 2%)
                            else if (wastage <= 1 && wastage > 0) {
                              return (wastage * 100).toFixed(2).replace(/\.00$/, '') + '%';
                            }
                            return '';
                          })()
                          : ''
                      }</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button type="submit" variant="contained" color="primary" size="large" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Receipt'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default WorkReceipt;